<!--PatternChart.svelte-->
<script lang="ts">
    import {
        createChart,
        CandlestickSeries,
        LineSeries,
        type CandlestickData,
        type IChartApi,
    } from 'lightweight-charts';
    import { PatternRenderer } from './PatternRenderer';
    import { PositionPrimitive } from './PositionPrimitive';
    import type { ApiPattern, IndicatorPoint } from '../api/klines';
    import type { SlTpOrder } from '../api/futures';
    import { onDestroy, onMount } from 'svelte';

    export interface LogicalRange { from: number; to: number; }

    interface Params {
        candles: CandlestickData[];
        apiPatterns: ApiPattern[];
        patterns: Record<string, boolean>;
        positions?: tradePosition[];
        slTpOrders?: SlTpOrder[];
        coin?: string;
        width?: number;
        height?: number;
        columnPerPage: number | string;
        indicators?: IndicatorPoint[];
        showEma9?:        boolean;
        showEma21?:       boolean;
        showEma200?:      boolean;
        showSupertrend?:  boolean;
        onLoadMore?: () => void;
        isLoadingMore?: boolean;
        initialRange?: LogicalRange | null;
        onRangeChange?: (range: LogicalRange) => void;
    }

    let {
        candles, apiPatterns, patterns, positions, slTpOrders, coin,
        width, height, columnPerPage,
        indicators, showEma9, showEma21, showEma200, showSupertrend,
        onLoadMore, isLoadingMore,
        initialRange, onRangeChange,
    }: Params = $props();
    let chartDiv: HTMLDivElement;

    let renderer: PatternRenderer | null = null;
    let positionPrimitive: PositionPrimitive | null = null;
    let chart: IChartApi;
    let series: any;
    let ema9Series: any;
    let ema21Series: any;
    let ema200Series: any;
    let stBullSeries: any;
    let stBearSeries: any;
    let ro: ResizeObserver | null = null;
    let prevCandleCount = 0;
    let _isLoadingMore = false;
    // Чи вже застосовували initialRange (робимо лише один раз при першому setData)
    let initialRangeApplied = false;

    onMount(() => {
        chart = createChart(chartDiv, {
            width: chartDiv.clientWidth || width || 300,
            height: height ?? 270,
            layout: { background: { color: '#fff' }, textColor: '#000' },
            grid: { vertLines: { color: '#e0e0e0' }, horzLines: { color: '#e0e0e0' } },
            crosshair: { mode: 1 },
            timeScale: { timeVisible: true, secondsVisible: false },
        });
        series = chart.addSeries(CandlestickSeries, {});

        ema9Series   = chart.addSeries(LineSeries, { color: '#2196f3', lineWidth: 1, priceLineVisible: false, lastValueVisible: false, title: 'EMA9'   });
        ema21Series  = chart.addSeries(LineSeries, { color: '#ff9800', lineWidth: 1, priceLineVisible: false, lastValueVisible: false, title: 'EMA21'  });
        ema200Series = chart.addSeries(LineSeries, { color: '#9c27b0', lineWidth: 2, priceLineVisible: false, lastValueVisible: false, title: 'EMA200' });
        stBullSeries = chart.addSeries(LineSeries, { color: '#26a69a', lineWidth: 2, priceLineVisible: false, lastValueVisible: false, title: 'ST↑' });
        stBearSeries = chart.addSeries(LineSeries, { color: '#ef5350', lineWidth: 2, priceLineVisible: false, lastValueVisible: false, title: 'ST↓' });

        chart.timeScale().fitContent();

        // Infinite scroll + збереження range
        let initialized = false;
        let lastLoadMore = 0;
        let saveRangeTimer: ReturnType<typeof setTimeout>;
        setTimeout(() => { initialized = true; }, 2000);

        chart.timeScale().subscribeVisibleLogicalRangeChange((range) => {
            if (!range) return;

            // Зберігаємо позицію з debounce 600мс
            if (initialized && onRangeChange) {
                clearTimeout(saveRangeTimer);
                saveRangeTimer = setTimeout(() => {
                    onRangeChange({ from: range.from, to: range.to });
                }, 600);
            }

            // Infinite scroll — тільки після ініціалізації і за межею даних
            if (!initialized || _isLoadingMore) return;
            if (range.from < -3) {
                const now = Date.now();
                if (now - lastLoadMore < 3000) return;
                lastLoadMore = now;
                onLoadMore?.();
            }
        });

        ro = new ResizeObserver(() => {
            const w = chartDiv.clientWidth;
            if (w > 0) chart?.resize(w, height ?? 270);
        });
        ro.observe(chartDiv);
    });

    onDestroy(() => {
        ro?.disconnect();
        chart?.remove();
    });

    $effect(() => {
        if (!series || !candles) return;

        const timeRange = chart.timeScale().getVisibleLogicalRange();

        // Sort by time asc and remove duplicates (lightweight-charts requires strict asc order)
        const sorted = [...candles]
            .sort((a, b) => (a.time as number) - (b.time as number))
            .filter((c, i, arr) => i === 0 || (c.time as number) !== (arr[i - 1].time as number));

        const newCount = sorted.length;
        const prepended = newCount - prevCandleCount;
        prevCandleCount = newCount;

        series.setData(sorted);

        // TradingView-style position overlay
        positionPrimitive?.detach();
        positionPrimitive = null;
        if (positions && coin) {
            const pos = positions.find(p => p.symbol === coin);
            if (pos) {
                const entry = parseFloat(pos.entryPrice);
                const liq   = parseFloat(pos.liquidationPrice);
                const posAmt = parseFloat(pos.positionAmt);
                if (!isNaN(entry)) {
                    const isLong  = posAmt > 0 || pos.positionSide === 'LONG';
                    const side    = isLong ? 'LONG' : 'SHORT';
                    const orders   = (slTpOrders ?? []).filter(o => o.symbol === coin);
                    const stopSide = isLong ? 'SELL' : 'BUY';
                    // Match by positionSide when available (Hedge Mode), fall back to side only (One-way Mode)
                    const matchesSide = (o: SlTpOrder) =>
                        o.side === stopSide &&
                        (o.positionSide === 'BOTH' || o.positionSide === side);
                    const slOrder  = orders.find(o => (o.type === 'STOP_MARKET'        || o.type === 'STOP')        && matchesSide(o));
                    const tpOrder  = orders.find(o => (o.type === 'TAKE_PROFIT_MARKET' || o.type === 'TAKE_PROFIT') && matchesSide(o));
                    const sl = slOrder ? parseFloat(slOrder.stopPrice) : (!isNaN(liq) ? liq : undefined);
                    const tp = tpOrder ? parseFloat(tpOrder.stopPrice) : undefined;

                    positionPrimitive = new PositionPrimitive({
                        entry,
                        stopLoss:      sl,
                        takeProfit:    tp,
                        side,
                        leverage:      pos.leverage ? parseInt(pos.leverage) : undefined,
                        unrealizedPnl: parseFloat(pos.unRealizedProfit),
                    });
                    series.attachPrimitive(positionPrimitive);
                }
            }
        }

        const patternsSnap = { ...patterns };
        renderer?.clear();
        renderer = new PatternRenderer(
            $state.snapshot(series),
            patternsSnap,
            chart
        );
        renderer.render($state.snapshot(apiPatterns) ?? []);

        // Відновлення збереженого viewport при першому завантаженні
        if (!initialRangeApplied && initialRange) {
            initialRangeApplied = true;
            chart.timeScale().setVisibleLogicalRange(initialRange);
        } else if (prepended > 0 && timeRange) {
            chart.timeScale().setVisibleLogicalRange({
                from: timeRange.from + prepended,
                to: timeRange.to + prepended,
            });
        } else if (timeRange) {
            chart.timeScale().setVisibleLogicalRange(timeRange);
        }
    });

    $effect(() => { _isLoadingMore = isLoadingMore ?? false; });

    $effect(() => {
        if (!ema9Series || !ema21Series || !ema200Series) return;
        const snap = $state.snapshot(indicators) ?? [];

        const toLine = (key: 'ema9' | 'ema21' | 'ema200') =>
            snap.filter(d => d[key] != null).map(d => ({ time: d.time as any, value: d[key]! }));

        ema9Series.setData(showEma9   ? toLine('ema9')   : []);
        ema21Series.setData(showEma21  ? toLine('ema21')  : []);
        ema200Series.setData(showEma200 ? toLine('ema200') : []);
    });

    $effect(() => {
        if (!stBullSeries || !stBearSeries) return;
        const snap = $state.snapshot(indicators) ?? [];

        if (!showSupertrend) {
            stBullSeries.setData([]);
            stBearSeries.setData([]);
            return;
        }

        const bullData = snap
            .filter(d => d.supertrend != null && d.supertrend_direction === 1)
            .map(d => ({ time: d.time as any, value: d.supertrend! }));
        const bearData = snap
            .filter(d => d.supertrend != null && d.supertrend_direction === -1)
            .map(d => ({ time: d.time as any, value: d.supertrend! }));

        stBullSeries.setData(bullData);
        stBearSeries.setData(bearData);
    });

</script>

<div bind:this={chartDiv}></div>
